import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Truck, Package, Weight, TrendingUp } from 'lucide-react';
import type { LoadingPlanMetrics } from '@/types/loading-plan';

interface LoadingPlanMetricsCardProps {
  metrics: LoadingPlanMetrics;
  vehicleName: string;
  palletTypeName: string;
  compact?: boolean;
}

export function LoadingPlanMetricsCard({ metrics, vehicleName, palletTypeName, compact }: LoadingPlanMetricsCardProps) {
  const utilizationColor =
    metrics.capacityUtilizationPercent > 90 ? 'text-red-600' :
    metrics.capacityUtilizationPercent > 75 ? 'text-yellow-600' :
    'text-green-600';

  if (compact) {
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 border rounded-xl bg-card shadow-sm flex-wrap">
        <div className="flex items-center gap-1.5 text-sm">
          <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-muted-foreground">Vehículo:</span>
          <span className="font-semibold">{vehicleName}</span>
        </div>
        <div className="h-4 w-px bg-border hidden sm:block" />
        <div className="text-sm">
          <span className="text-muted-foreground">Tipo Palet: </span>
          <span className="font-semibold">{palletTypeName}</span>
        </div>
        <div className="h-4 w-px bg-border hidden sm:block" />
        <div className="flex items-center gap-2 text-sm">
          <Package className="h-4 w-4 text-blue-500 shrink-0" />
          <span className="font-semibold text-blue-600">{metrics.totalPallets}</span>
          <span className="text-muted-foreground">/ {metrics.maxPallets} palets</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-purple-600 font-medium">{metrics.totalVirtualPallets} virt.</span>
          <span className="text-muted-foreground">/</span>
          <span className="text-green-600 font-medium">{metrics.totalPhysicalPallets} fís.</span>
        </div>
        <div className="h-4 w-px bg-border hidden sm:block" />
        <div className="flex items-center gap-1.5 text-sm">
          <Weight className="h-4 w-4 text-orange-500 shrink-0" />
          <span className="font-semibold">{metrics.totalWeightKg.toFixed(0)} kg</span>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-40">
          <Progress value={Number(metrics.capacityUtilizationPercent)} className="h-2 flex-1" />
          <span className={`text-sm font-bold tabular-nums ${utilizationColor}`}>
            {metrics.capacityUtilizationPercent.toFixed(0)}%
          </span>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5" />
          Resumen del Plan de Carga
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Información del vehículo */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
          <div>
            <p className="text-sm text-muted-foreground">Vehículo</p>
            <p className="font-semibold">{vehicleName}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Tipo de Palet</p>
            <p className="font-semibold">{palletTypeName}</p>
          </div>
        </div>
        
        {/* Métricas principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex flex-col items-center p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <Package className="h-5 w-5 text-blue-600 dark:text-blue-400 mb-2" />
            <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {metrics.totalPallets}
            </span>
            <span className="text-sm text-muted-foreground">Palets Totales</span>
          </div>
          
          <div className="flex flex-col items-center p-4 bg-purple-50 dark:bg-purple-950 rounded-lg">
            <Package className="h-5 w-5 text-purple-600 dark:text-purple-400 mb-2" />
            <span className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {metrics.totalVirtualPallets}
            </span>
            <span className="text-sm text-muted-foreground">Virtuales</span>
          </div>
          
          <div className="flex flex-col items-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
            <Package className="h-5 w-5 text-green-600 dark:text-green-400 mb-2" />
            <span className="text-3xl font-bold text-green-600 dark:text-green-400">
              {metrics.totalPhysicalPallets}
            </span>
            <span className="text-sm text-muted-foreground">Físicos</span>
          </div>
          
          <div className="flex flex-col items-center p-4 bg-orange-50 dark:bg-orange-950 rounded-lg">
            <Weight className="h-5 w-5 text-orange-600 dark:text-orange-400 mb-2" />
            <span className="text-3xl font-bold text-orange-600 dark:text-orange-400">
              {metrics.totalWeightKg.toFixed(0)}
            </span>
            <span className="text-sm text-muted-foreground">kg Totales</span>
          </div>
        </div>
        
        {/* Utilización de capacidad */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Capacidad del Camión (Máx: {metrics.maxPallets ?? 0} palets)</span>
            </div>
            <span className={`text-2xl font-bold ${utilizationColor}`}>
              {metrics.totalPallets} / {metrics.maxPallets} ({metrics.capacityUtilizationPercent.toFixed(1)}%)
            </span>
          </div>
          <Progress value={metrics.capacityUtilizationPercent} className="h-3" />
          <p className="text-xs text-muted-foreground">
            {metrics.totalPallets > metrics.maxPallets ? '⚠️ EXCESO DE CAPACIDAD' : ''}
            {metrics.totalPallets <= metrics.maxPallets && metrics.capacityUtilizationPercent > 90 && '⚠️ Capacidad casi completa'}
            {metrics.capacityUtilizationPercent > 75 && metrics.capacityUtilizationPercent <= 90 && '✓ Buena utilización'}
            {metrics.capacityUtilizationPercent <= 75 && '💡 Espacio disponible'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
